// Improve a page's and Write something new's summary panel and the line under their list, worked out again in
// the browser as the list's rows change (P5 of documentation/pr-plans/insights-simplify-plan.md: D52, D121,
// B11). Once a list is loaded, Alpine owns it (plan 4.14): P3's write routes fold, bring back and re-rank rows
// with no Livewire call, so no summary event comes from the server after them, and the panel kept the figures
// it was drawn with. The table says when its rows change (ROWS_EVENT); the panel works its summary out from
// them, as OpportunityActivity::summary() does from the activity's rows, and sends it as the activity's
// summary event, which the panel and the line under the list already follow. The server's summary carries
// what only it knows (`live`: the read's count of target keywords, the folds it already counts, the words and
// the workspace's thousands separator). OpportunityActivitySummaryParityTest pins the two to each other.

export const SUMMARY_EVENT = "insights-activity-summary";

// The table's rows changed: {table, rows, notices, closed} (insights-activity-table.js, ActivityTable::ROWS_EVENT).
export const ROWS_EVENT = "activity-table-rows";

// OpportunityScores::stageShares()'s keys, in its order, which breaks a tie between equal remainders.
const STAGE_KEYS = ["bottom", "middle", "top", "not_sorted"];
const TOP = "top";
const NOT_SORTED = "not_sorted";

// The panel of one activity (activity-summary.blade.php): its summary, as the server drew it and as either
// side works it out again.
export default function InsightsActivitySummary(activity, summary) {
    return {
        summary,

        // The activity's summary event, from the server after a Livewire action or from recount().
        hear(detail) {
            if (detail && detail.activity === activity && detail.summary && typeof detail.summary === "object") {
                this.summary = detail.summary;
            }
        },

        // The table this panel follows changed its rows: the summary is worked out again and sent as the
        // activity's event, so the line under the list follows as the panel does.
        recount(detail) {
            const live = this.summary?.live;

            if (!live || !detail || detail.table !== live.table || typeof window === "undefined") {
                return;
            }

            window.dispatchEvent(new CustomEvent(SUMMARY_EVENT, { detail: { activity, summary: summarize(this.summary, detail) } }));
        },
    };
}

/**
 * The summary for the table's rows as they stand, shaped as OpportunityActivity::summary() shapes it: the
 * rows still to do (no notice) count and add their Potential clicks; every fold this visit made counts as
 * done, a closed notice's too, since closing it undid nothing; and the line under the list follows the
 * targets this visit made against those the read already counts (leftOutLine()).
 *
 * @param {object} summary the summary drawn, with its `live` part
 * @param {{rows: object[], notices: object, closed: object}} detail the table's rows, its folds by row id
 *        ({kind, undo}), and the folds of rows whose notice was closed
 */
export function summarize(summary, detail) {
    const live = summary.live;
    const rows = Array.isArray(detail.rows) ? detail.rows : [];
    const notices = detail.notices && typeof detail.notices === "object" ? detail.notices : {};
    const closed = detail.closed && typeof detail.closed === "object" ? detail.closed : {};
    const open = rows.filter((row) => !notices[row.id]);
    const folds = new Map([...rows.filter((row) => notices[row.id]).map((row) => [row.id, notices[row.id]]), ...Object.entries(closed)]);
    const count = (value) => grouped(value, live.thousands);
    const possible = open.reduce((sum, row) => sum + figure(row.cells?.potential?.value), 0);
    // A figure that rounds to nothing still qualified, so it reads "Under 1", never +0.
    const isUnderOne = possible > 0 && Math.round(possible) < 1;
    const linked = open.find((row) => Number(row.topicId) > 0) ?? null;

    return {
        ...summary,
        rows: open.length,
        // The list's order is said by the table now (its orderNote, P21), so the line holds the two counts.
        meta: [fill(live.words.toDo, count(open.length)), folds.size > 0 ? fill(live.words.done, count(folds.size)) : null]
            .filter((part) => typeof part === "string" && part !== "")
            .join(" · "),
        tiles: summary.tiles.map((tile) => (tile.key === "possible" ? { ...tile, value: isUnderOne ? live.words.underOne : `+${count(Math.round(possible))}`, countUp: !isUnderOne } : tile)),
        note: isLearningHeavy(stageShares(open)) ? live.words.note : null,
        link: linked?.detail?.topic?.url ?? null,
        leftOut: leftOutLine(live, open, folds, count),
    };
}

// The searches left out as target keywords Check my results is still waiting to judge
// (OpportunityActivity::leftOutLine(), D150): the read's count, less the folds it already counted that are back
// on the list, plus the Done folds this visit made since. Every Done (a fold that names no kind is one) counts:
// it makes a target, starts a judged one's check again, or finds one made after the list was read, and each
// then waits to be judged, so the next read leaves it out too (OpportunityActivity::madeTargets()).
function leftOutLine(live, open, folds, count) {
    const made = [...folds].filter(([, fold]) => (fold.kind ?? "done") === "done").map(([id]) => id);
    const held = Array.isArray(live.heldTargets) ? live.heldTargets : made;
    const openIds = new Set(open.map((row) => row.id));
    const left = Number(live.targeted ?? 0) - held.filter((id) => openIds.has(id)).length + made.filter((id) => !held.includes(id)).length;

    return left > 0 ? fill(left === 1 ? live.words.leftOutOne : live.words.leftOutMany, count(left)) : null;
}

// OpportunityScores::stageShares(): each stage's share of the open rows, by Potential clicks and by score.
function stageShares(rows) {
    const clicks = Object.fromEntries(STAGE_KEYS.map((key) => [key, 0]));
    const scores = { ...clicks };

    rows.forEach((row) => {
        const key = row.stage ?? NOT_SORTED;

        // No list holds a Not relevant search (D47), which the server refuses to share out.
        if (key in clicks) {
            clicks[key] += figure(row.cells?.potential?.value);
            scores[key] += figure(row.rawScore?.value);
        }
    });

    return { potentialClicks: wholePercents(clicks), score: wholePercents(scores) };
}

// OpportunityScores::wholePercents(): floors, then the largest remainders take the points left, a tie to the
// earlier key (both sorts are stable).
function wholePercents(sums) {
    const keys = Object.keys(sums);
    const total = keys.reduce((sum, key) => sum + sums[key], 0);

    if (total <= 0) {
        return Object.fromEntries(keys.map((key) => [key, 0]));
    }

    const percents = {};
    const remainders = {};

    keys.forEach((key) => {
        const exact = (sums[key] / total) * 100;

        percents[key] = Math.floor(exact);
        remainders[key] = exact - percents[key];
    });

    const left = 100 - keys.reduce((sum, key) => sum + percents[key], 0);

    [...keys].sort((a, b) => remainders[b] - remainders[a]).slice(0, left).forEach((key) => {
        percents[key]++;
    });

    return percents;
}

// OpportunityActivity::isLearningHeavy(): Top of funnel brings the most Potential clicks of any stage, and
// the score gives it a smaller share than that.
function isLearningHeavy(shares) {
    const clicks = shares.potentialClicks;
    const others = Object.keys(clicks).filter((key) => key !== TOP).map((key) => clicks[key]);

    return clicks[TOP] > Math.max(...others) && clicks[TOP] > shares.score[TOP];
}

// SiteAnalyticsFormatter's Count: rounded, its digits grouped in threes by the workspace's separator. The
// figures here are counts, never negative.
function grouped(value, thousands) {
    return String(Math.round(Math.abs(value))).replace(/\B(?=(\d{3})+(?!\d))/g, () => thousands ?? ",");
}

function fill(words, count) {
    return String(words ?? "").split(":count").join(count);
}

function figure(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
